/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Config } from 'src/app/config';
import { Logger } from 'src/app/core/logger';
import { FieldOptionModel } from 'src/app/models/widgets/field-option.model';
import { WidgetsRadioModel } from 'src/app/models/widgets/widgets-radio.model';
import { MetadataTemplateService } from 'src/app/services/metadata/metadata-template.service';
import { getKeyFromText } from 'src/app/services/shared/camelCase';
import { WidgetsMetadataService } from 'src/app/services/widgets/widgets-metadata.service';
import { SharedConstants } from 'src/app/shared/constants/shared-constant';

@Component({
  selector: 'app-metadata-radio',
  templateUrl: './metadata-radio.component.html',
  styleUrls: ['./metadata-radio.component.scss']
})
export class MetadataRadioComponent implements OnInit {
  @Input() data: WidgetsRadioModel = new WidgetsRadioModel();
  template?: string;
  // https://stackblitz.com/edit/form-array-angular-yvow9n?file=src%2Fapp%2Fapp.component.ts
  // https://stackblitz.com/edit/angular-forms-formarray-example-leedar?file=src%2Fapp%2Fapp.component.html
  form: FormGroup;
  nextValue: number = 1;
  public errorMessage: string = "";
  commentFieldGroup: any= [];

  constructor(
    private fb: FormBuilder,
    public cd: ChangeDetectorRef,
    private widgetsMetadataService: WidgetsMetadataService,
    private templateService: MetadataTemplateService,
    private logger: Logger) {
    // Testing: We need the options instantiated
    this.form = this.fb.group({
      heading: ['', Validators.maxLength(30)],
      description: ['', Validators.maxLength(100)],
      reqCheck: [this.data.required],
      options: this.fb.array([]),
      otherFlag: [false, [Validators.required]],
      commentKey: ['', Validators.maxLength(30)],
      commentFieldOn: [null, [Validators.required]],
      commentFieldOnOptional: [false, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      heading: ['', Validators.maxLength(30)],
      description: ['', Validators.maxLength(100)],
      reqCheck: [this.data.required],
      options: this.fb.array([]),
      otherFlag: [false, [Validators.required]],
      commentKey: [this.data.commentKey, Validators.maxLength(30)],
      commentFieldOn: [null, [Validators.required]],
      commentFieldOnOptional: [false, [Validators.required]],
      commentFieldOff: [null, [Validators.required]]
    });

    const options = this.data.options;
    // If no options provided, add yes/no as a option(setInitialValues())
    if ((!options || options.length === 0) && !this.data.deleteAllFlag) { 
      this.setInitialValues();
    }

    // Options initial values
    options?.forEach((option, ind) => {
      const add = this.form.get('options') as FormArray;

      if (option.value && option.label) {
        // Formly > V5.5 - Uses {value, label}
        add.push(this.fb.group({
          // value: [ind, [Validators.max(3000), Validators.required]],
          value: [option.value, [Validators.max(3000), Validators.required]],
          label: [option.label, [Validators.maxLength(SharedConstants.defaultOtherLabelLength), Validators.required]],
          selected: [option.selected],
          disabled: [option.disabled]
        }));
      } else if (option.key) {
        // Formly < V5.5 - Uses {value, key}
        add.push(this.fb.group({
          value: [getKeyFromText(option.key), [Validators.max(3000), Validators.required]],
          label: [option.key, [Validators.maxLength(SharedConstants.defaultOtherLabelLength), Validators.required]],
          selected: [option.selected],
          disabled: [option.disabled]
        }));
      }
    });

    // Initial value
    if (this.data.commentFieldOn) {
      this.form.controls['commentFieldOn'].setValue(this.data.commentFieldOn);
    } else {
      this.form.controls['commentFieldOn'].setValue(null);
    }
    if (this.data.commentFieldOff) {
      this.form.controls['commentFieldOff'].setValue(this.data.commentFieldOff);
    } else {
      this.form.controls['commentFieldOff'].setValue(null);
    }
    this.form.controls['commentKey'].setValue(this.data.commentKey ? this.data.commentKey : null);
    this.form.controls['commentFieldOnOptional'].setValue(this.data.commentFieldOnOptional);
    this.form.controls['commentFieldOn'].setValue(this.data.commentFieldOn ? this.data.commentFieldOn : null);      
    this.form.controls['reqCheck'].setValue(this.data.required);
    this.form.controls['otherFlag'].setValue(this.data.otherFlag);
    // Clear all options flag
    if(this.data.deleteAllFlag === true){
      this.data.commentFieldOn = '';
      this.data.deleteAllFlag = false;
    }
    this.initCommentField();
  }

  setInitialValues() {
    const add = this.form.get('options') as FormArray;
    add.push(this.fb.group({
      value: ['Yes', [Validators.max(3000), Validators.required]],
      label: ['Yes', [Validators.maxLength(SharedConstants.defaultOtherLabelLength), Validators.required]],
      selected: [false],
      disabled: [false]
    }));
    add.push(this.fb.group({
      value: ['No', [Validators.required]],
      label: ['No', [Validators.required]],
      selected: [false],
      disabled: [false]
    }));
    this.nextValue = add.length;
    // Save option values
    this.onSaveOptions(this.nextValue);
  }

  onAddNewOption() {
    const add = this.form.get('options') as FormArray;
    this.nextValue = add.length;
    add.push(this.fb.group({
      value: [''+this.nextValue, [Validators.max(3000), Validators.required]],
      label: ['Option'+this.nextValue, [Validators.maxLength(SharedConstants.defaultOtherLabelLength), Validators.required]],
      selected: [false],
      disabled: [false]
    }));
    this.onSaveOptions(this.nextValue);
  }

  onDeleteOption(selectedIndex: number): void {
    const add = this.form.get('options') as FormArray;
    add.removeAt(selectedIndex);
    // Reset data option after delete from form
    this.nextValue = add.length;
    // Delete all options reset
    if(!this.nextValue) this.data.deleteAllFlag = true;
    this.onSaveOptions(this.nextValue);
  }

  onSaveOptions(currIndex) {
    try {
      const val = this.form.value;
      const options: FieldOptionModel[] = val.options as FieldOptionModel[];
      this.errorMessage = "";

      // Value input field should have unique value.
      this.checkUniqueValues(options, currIndex);

      this.data.options = options;
      this.widgetsMetadataService.showMetadata(this.data);
    } catch (err) {
      this.logger.log("onOptionEdited " + err);
    }
  }

  private checkUniqueValues(options: FieldOptionModel[], currIndex) {
    const uniqueValues = new Set(options.map(v => v.value));
    const childOptions = this.form.get('options') as FormArray;

    // condition for duplicate values.
    if (uniqueValues.size < options.length) {
      this.errorMessage = Config.CONSTANTS.errorRepeatedValue;

      // This didn't work: check
      const tryToAutoCorrectValueErrors = false;
      if (tryToAutoCorrectValueErrors) {
        for (let k = 0; k < childOptions.length; k++) {
          if (k === currIndex) {
            childOptions.at(k).get('value')?.setValue("");
          }
        }
      }
    }
  
    // Update data
    const newOptions: FieldOptionModel[] = [];
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let j = 0; j < childOptions.controls.length; j++) {
      // j is used, so for-of could not be used.
      newOptions.push(childOptions.controls[j].value);
    }

    // TODO: We change options here. Command pattern?
    this.data.options = newOptions;
  }

  onChangeLabel(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.label = this.form.controls['heading'].value as string;
    } catch (error) {
      console.log("onChangeLabel "+error);
    }
  }

  onChangeDescription(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.description = this.form.controls['description'].value as string;
    } catch (error) {
      console.log("onChangeDescription "+error);
    }
  }

  onChangeRequired(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.required = this.form.controls['reqCheck'].value as boolean;
    } catch (ignore) { }
  }

  onChangeOtherFlag(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.otherFlag = this.form.controls['otherFlag'].value as boolean;
    } catch (error) {
      console.log("onChangeOtherFlag "+error);
    }
  }

  onCommentsRequired(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      const selectedComments: any = this.form.controls['commentFieldOn'].value;
      this.data.commentFieldOn = selectedComments;
    } catch (error) {
      console.log("onCommentsRequired "+error);
    }
  }

  onChangeCommentKey(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.commentKey = this.form.controls['commentKey'].value as string;
    } catch (error) {
      console.log("commentKey "+error);
    }
  }

  onChangeCommentFieldOnOptional(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.commentFieldOnOptional = this.form.controls['commentFieldOnOptional'].value as boolean;
    } catch (error) {
      console.log("commentFieldOnOptional "+error);
    }
  }

  onCommentsNotRequired(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      const selectedComments: any = this.form.controls['commentFieldOff'].value;
      this.data.commentFieldOff = selectedComments;
    } catch (error) {
      console.log("onCommentsNotRequired "+error);
    }
  }

  onSelectOption(selectedIndex) {
    try {
      const options = this.form.get('options') as FormArray;

      // only one selected checkbox should be checked at a time.
      for (let k = 0; k < options.length; k++) {
        if (k !== selectedIndex) {
          options.at(k).get('selected')?.setValue(false);
        }
      }
      const newOptions: FieldOptionModel[] = [];
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let j = 0; j < options.controls.length; j++) {
        // j is used, so for-of could not be used.
        newOptions.push(options.controls[j].value);
      }
      //TODO commands
      this.data.options = newOptions;
    } catch (err) {
      this.logger.log("onOptionEdited " + err);
    }

  }

  getOptions(): FormControl {
    return (this.form.get('options') as FormControl);
  }

  getOptionsValues(): any[] {
    const opts = this.form.controls.options.value;
    return opts;
  }

  onClearAllOptions() {
    const add = this.form.get('options') as FormArray;
    while (add.length !== 0) {
      add.removeAt(0);
    }
    this.nextValue = add.length;
    // Option reset  
    this.data.options = [];
    this.data.deleteAllFlag = true;
    this.widgetsMetadataService.showMetadata(this.data);
  }

  /**
   * Called when we 
   * @param modifiedTemplate Structure that contains a string template 
   *    along with conditional related properties
   */
  getModifiedTemplate(modifiedTemplate) {
    this.data = this.templateService.getModifiedTemplate(modifiedTemplate, this.data);
    // Trigger value change
    this.template = this.data.template;
  }

  /**
   * Initialization for conditional comment checkbox conversion
   */
  initCommentField(){
    let commentFieldArr = this.data.commentFieldOn?.split(";");
    this.data.options?.forEach(element => {
      this.commentFieldGroup.push({ 
          label: element.label, 
          value: element.value,
          checked: commentFieldArr && commentFieldArr.includes(element.value) ? true : false
        });
    });
  }

  /** 
   * Conditional comment checkbox state
   */
  changeCommentStatus(item){
    item.checked = !item.checked;
    this.data.commentFieldOn = this.commentFieldGroup.filter((item)=> item.checked).map(({value}) => value).join(";");
  }
}
